import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import salesContinuousServiceService from '../../../services/salesContinuousServiceService';
import CloseServiceModal from './CloseServiceModal';
import Toast from '../../../components/Common/Toast';
import MutateGate from '../../../components/Common/MutateGate';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import { formatDateLocaleLima } from '../../../utils/dateUtils';
import IgvYesNoSelect from '../../../components/Ventas/IgvYesNoSelect';
import IgvRateSelect from '../../../components/Ventas/IgvRateSelect';
import {
  calcVentasLine,
  formatIgvRateLabel,
  parseIgvRate,
} from '../../../utils/ventasIgvUtils';
import { isItemTaxable } from '../../../utils/priceIgvUtils';
import SalesContinuousServicePdfButton from '../../../components/Ventas/SalesContinuousServicePdfButton';
import SalesContinuousServicePrintButton from '../../../components/Ventas/SalesContinuousServicePrintButton';

const roundTo4 = (v) => Math.round(Number(v) * 10000) / 10000;

const VentasContinuousServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [progressToDelete, setProgressToDelete] = useState(null);
  const lastLoadedId = useRef(null);

  const [progressForm, setProgressForm] = useState({
    progress_date: new Date().toISOString().split('T')[0],
    description: '',
    quantity: '1',
    unit_price: '',
    has_igv: true,
    tax_rate: '18',
  });
  const [progressErrors, setProgressErrors] = useState({});

  useEffect(() => {
    if (lastLoadedId.current !== id) {
      lastLoadedId.current = id;
      loadService();
    }
  }, [id]);

  const sortProgress = (list) =>
    [...(list || [])].sort(
      (a, b) => new Date(a.progress_date || 0) - new Date(b.progress_date || 0)
    );

  const loadService = async () => {
    setLoading(true);
    try {
      const response = await salesContinuousServiceService.getContinuousService(id);
      if (response.success) {
        setService(response.data);
        setProgress(sortProgress(response.data.progress));
      } else {
        setToast({ isOpen: true, message: response.message || 'Servicio no encontrado', type: 'error' });
        setTimeout(() => navigate('/ventas/continuous-services', { state: { posConfirmed: true } }), 2000);
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar el servicio', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(amount || 0);

  const calcProgressTotals = () => {
    const taxRate = progressForm.has_igv ? parseIgvRate(progressForm.tax_rate) : 0;
    const line = calcVentasLine(
      {
        quantity: progressForm.quantity,
        unit_price: progressForm.unit_price,
        has_igv: progressForm.has_igv,
      },
      taxRate,
    );
    return {
      subtotal: line.subtotal,
      taxRate: line.hasIgv ? taxRate : 0,
      taxAmount: line.igvAmount,
      total: line.total,
    };
  };

  const handleAddProgress = async (e) => {
    e.preventDefault();
    if (service?.status !== 'open') return;

    const newErrors = {};
    if (!progressForm.progress_date) newErrors.progress_date = 'Fecha requerida';
    if (!progressForm.quantity || parseFloat(progressForm.quantity) <= 0) {
      newErrors.quantity = 'Cantidad inválida';
    }
    if (progressForm.unit_price === '' || parseFloat(progressForm.unit_price) < 0) {
      newErrors.unit_price = 'Precio requerido';
    }
    setProgressErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoadingAction(true);
    try {
      const totals = calcProgressTotals();
      const response = await salesContinuousServiceService.createProgress(id, {
        progress_date: progressForm.progress_date,
        description: progressForm.description || null,
        quantity: roundTo4(progressForm.quantity),
        unit_price: roundTo4(progressForm.unit_price),
        has_igv: progressForm.has_igv,
        tax_rate: totals.taxRate,
      });
      if (response.success) {
        setToast({ isOpen: true, message: 'Avance registrado', type: 'success' });
        setProgressForm({
          progress_date: new Date().toISOString().split('T')[0],
          description: '',
          quantity: '1',
          unit_price: '',
          has_igv: true,
          tax_rate: '18',
        });
        loadService();
      } else {
        setProgressErrors(response.errors || {});
        setToast({ isOpen: true, message: response.message || 'Error al registrar', type: 'error' });
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al registrar avance', type: 'error' });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDeleteProgress = async () => {
    if (!progressToDelete) return;
    setLoadingAction(true);
    try {
      const response = await salesContinuousServiceService.deleteProgress(id, progressToDelete);
      if (response.success) {
        setToast({ isOpen: true, message: 'Registro eliminado', type: 'success' });
        setProgressToDelete(null);
        loadService();
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al eliminar', type: 'error' });
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al eliminar registro', type: 'error' });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCloseService = async () => {
    setLoadingAction(true);
    try {
      const response = await salesContinuousServiceService.closeContinuousService(id);
      if (response.success) {
        setToast({ isOpen: true, message: 'Servicio cerrado', type: 'success' });
        setIsCloseModalOpen(false);
        loadService();
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cerrar', type: 'error' });
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cerrar servicio', type: 'error' });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleOpenService = async () => {
    setLoadingAction(true);
    try {
      const response = await salesContinuousServiceService.openContinuousService(id);
      if (response.success) {
        setToast({ isOpen: true, message: 'Servicio reabierto', type: 'success' });
        loadService();
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al reabrir', type: 'error' });
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al reabrir servicio', type: 'error' });
    } finally {
      setLoadingAction(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Cargando...</div>;
  }

  if (!service) return null;

  const isOpen = service.status === 'open';

  const previewTotals = calcProgressTotals();

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-wrap justify-between items-start gap-4">
          <div>
            <Link
              to="/ventas/continuous-services"
              state={{ posConfirmed: true }}
              className="text-sm text-teal-600 hover:text-teal-800 mb-2 inline-block"
            >
              ← Volver al listado
            </Link>
            <h1 className="text-2xl font-bold text-gray-800">{service.code}</h1>
            <p className="text-gray-600 mt-1">{service.client?.name || 'Sin cliente'}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SalesContinuousServicePrintButton serviceId={service.id} />
            <SalesContinuousServicePdfButton
              serviceId={service.id}
              showLabel
              onSuccess={() =>
                setToast({
                  isOpen: true,
                  message: `PDF de ${service.code} descargado`,
                  type: 'success',
                })
              }
              onError={(message) =>
                setToast({
                  isOpen: true,
                  message: message || 'Error al generar el PDF',
                  type: 'error',
                })
              }
            />
            {isOpen ? (
              <MutateGate>
                <button
                  type="button"
                  onClick={() => setIsCloseModalOpen(true)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 text-sm font-medium"
                >
                  Cerrar servicio
                </button>
              </MutateGate>
            ) : (
              <MutateGate>
                <button
                  type="button"
                  onClick={handleOpenService}
                  disabled={loadingAction}
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm font-medium disabled:opacity-50"
                >
                  Reabrir servicio
                </button>
              </MutateGate>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <div className="text-xs text-gray-500 uppercase">Estado</div>
            <div className="mt-1">
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  isOpen ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}
              >
                {isOpen ? 'Abierto' : 'Cerrado'}
              </span>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Fecha inicio</div>
            <div className="mt-1 text-sm">{formatDateLocaleLima(service.start_date)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Fecha fin</div>
            <div className="mt-1 text-sm">
              {service.end_date ? formatDateLocaleLima(service.end_date) : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Base imponible</div>
            <div className="mt-1 text-sm">{formatCurrency(service.base_imponible)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">IGV</div>
            <div className="mt-1 text-sm">{formatCurrency(service.igv_amount)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Monto acumulado</div>
            <div className="mt-1 text-sm font-semibold text-teal-700">
              {formatCurrency(service.total_amount)}
            </div>
          </div>
          {service.notes && (
            <div className="md:col-span-2 lg:col-span-3 xl:col-span-6">
              <div className="text-xs text-gray-500 uppercase">Observaciones</div>
              <div className="mt-1 text-sm text-gray-700">{service.notes}</div>
            </div>
          )}
        </div>

        {isOpen && (
          <MutateGate>
            <form
              onSubmit={handleAddProgress}
              className="bg-white rounded-lg border border-gray-200 p-6 mb-6"
            >
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Agregar registro de avance</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                  <input
                    type="date"
                    value={progressForm.progress_date}
                    onChange={(e) =>
                      setProgressForm((prev) => ({ ...prev, progress_date: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {progressErrors.progress_date && (
                    <p className="text-xs text-red-600 mt-1">{progressErrors.progress_date}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad</label>
                  <input
                    type="number"
                    min="0.001"
                    step="any"
                    value={progressForm.quantity}
                    onChange={(e) =>
                      setProgressForm((prev) => ({ ...prev, quantity: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {progressErrors.quantity && (
                    <p className="text-xs text-red-600 mt-1">{progressErrors.quantity}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio unit.</label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={progressForm.unit_price}
                    onChange={(e) =>
                      setProgressForm((prev) => ({ ...prev, unit_price: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  {progressErrors.unit_price && (
                    <p className="text-xs text-red-600 mt-1">{progressErrors.unit_price}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <input
                    type="text"
                    value={progressForm.description}
                    onChange={(e) =>
                      setProgressForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IGV</label>
                  <IgvYesNoSelect
                    value={progressForm.has_igv}
                    onChange={(val) => setProgressForm((prev) => ({ ...prev, has_igv: val }))}
                  />
                </div>
                <div>
                  <IgvRateSelect
                    value={progressForm.tax_rate}
                    onChange={(value) => setProgressForm((prev) => ({ ...prev, tax_rate: value }))}
                    disabled={!progressForm.has_igv}
                    showLabel
                    label="Tasa de IGV"
                  />
                </div>
                <div className="flex flex-col justify-end gap-2">
                  <button
                    type="submit"
                    disabled={loadingAction}
                    className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm font-medium disabled:opacity-50"
                  >
                    {loadingAction ? 'Guardando...' : 'Agregar'}
                  </button>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap justify-end gap-x-6 gap-y-1 text-sm">
                <div>
                  <span className="text-gray-600">Subtotal: </span>
                  <span className="font-medium">{formatCurrency(previewTotals.subtotal)}</span>
                </div>
                <div>
                  <span className="text-gray-600">
                    IGV ({previewTotals.taxRate ? formatIgvRateLabel(previewTotals.taxRate) : '—'}):{' '}
                  </span>
                  <span className="font-medium">{formatCurrency(previewTotals.taxAmount)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total: </span>
                  <span className="font-semibold text-teal-700">{formatCurrency(previewTotals.total)}</span>
                </div>
              </div>
            </form>
          </MutateGate>
        )}

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Registros de avance</h2>
          </div>
          {progress.length === 0 ? (
            <p className="p-8 text-center text-gray-500">No hay registros de avance</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cant.</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">P. unit.</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">IGV</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">IGV %</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto IGV</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  {isOpen && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {progress.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDateLocaleLima(row.progress_date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{row.description || '—'}</td>
                    <td className="px-6 py-4 text-sm text-right">{row.quantity}</td>
                    <td className="px-6 py-4 text-sm text-right">{formatCurrency(row.unit_price)}</td>
                    <td className="px-6 py-4 text-sm text-center">
                      {isItemTaxable(row.has_igv) ? 'Sí' : 'No'}
                    </td>
                    <td className="px-6 py-4 text-sm text-center text-gray-600">
                      {isItemTaxable(row.has_igv) && row.tax_rate
                        ? formatIgvRateLabel(row.tax_rate)
                        : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-right">{formatCurrency(row.subtotal)}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-700">
                      {isItemTaxable(row.has_igv) ? formatCurrency(row.tax_amount) : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-medium">
                      {formatCurrency(row.total)}
                    </td>
                    {isOpen && (
                      <td className="px-6 py-4 text-right">
                        <MutateGate>
                          <button
                            type="button"
                            onClick={() => setProgressToDelete(row.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Eliminar
                          </button>
                        </MutateGate>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <CloseServiceModal
        isOpen={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        onConfirm={handleCloseService}
        serviceName={service.code}
        loading={loadingAction}
      />

      <ConfirmModal
        isOpen={!!progressToDelete}
        onClose={() => setProgressToDelete(null)}
        onConfirm={handleDeleteProgress}
        title="Eliminar registro de avance"
        message="¿Eliminar este registro de avance? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
        isLoading={loadingAction}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default VentasContinuousServiceDetail;
